import { useEffect, useMemo, useState } from 'react';
import { DialFace } from '../dial/Dial';
import { useAnnounce, useFocusOnChange, useSweep } from '../ui/shell';
import { SOLO, SOLO_OPENERS, SOLO_ROUNDS, type SoloCard } from '@shared/content';
import { dial10, pointsFor, quantise, shuffle } from '@shared/scoring';

/**
 * Solo: ten authored challenges, offline, no server. The "correct" answer is
 * one person's authored intent, so disagreeing with a solo answer is the game
 * working, not a bug — which is why every card carries the author's reasoning
 * at reveal. It is the closest thing solo has to an argument.
 */
function dealSolo(): SoloCard[] {
  // The first clue anyone ever sees is the acquisition moment, so it comes from
  // the curated opener set: mid-band target, unambiguous axis. A third of the
  // deck is polar, and opening on one teaches that the ends of the dial are
  // where the game lives — the opposite of the truth.
  const openers = SOLO.filter((c) => SOLO_OPENERS.includes(c[2]));
  const rest = SOLO.filter((c) => !SOLO_OPENERS.includes(c[2]));
  const first = shuffle(openers)[0];
  return [first, ...shuffle(rest).slice(0, SOLO_ROUNDS - 1)];
}

export function Solo({ onExit }: { onExit: () => void }) {
  const [deck] = useState(dealSolo);
  const [index, setIndex] = useState(0);
  const [value, setValue] = useState(50);
  const [touched, setTouched] = useState(false);
  const [locked, setLocked] = useState<number | null>(null);
  const [log, setLog] = useState<{ clue: string; guess: number; target: number; points: number }[]>([]);
  const announce = useAnnounce();

  const card = deck[index];
  const done = index >= deck.length;
  const total = log.reduce((a, r) => a + r.points, 0);
  const headingRef = useFocusOnChange<HTMLHeadingElement>(`${index}:${locked !== null}:${done}`);

  if (done) {
    const band =
      total >= 30 ? 'Reading minds.' : total >= 20 ? 'Solid instincts.' : total >= 10 ? 'You got the gist.' : 'Rough night.';
    return (
      <section className="screen">
        <div className="bar">
          <span className="eyebrow">Solo</span>
          <span className="eyebrow">{deck.length} clues</span>
        </div>
        <div className="body-area top">
          <div>
            <h1 className="final-h" tabIndex={-1} ref={headingRef}>
              {total} of 40
            </h1>
            <p className="final-sub">{band}</p>
          </div>
          <ul className="scores">
            {log.map((r, i) => (
              <li key={i} className={r.points === 0 ? 'zero' : undefined}>
                <span className="tag">{dial10(r.target)}</span>
                <span className="who">{r.clue}</span>
                <span className="pts">
                  {r.points > 0 ? '+' : ''}
                  {r.points}
                </span>
                <span className="tot">{dial10(r.guess)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="actions">
          <ShareScore total={total} />
          <button type="button" className="btn ghost" onClick={onExit}>
            Back to the start
          </button>
        </div>
      </section>
    );
  }

  if (locked !== null) {
    return (
      <SoloReveal
        card={card}
        guess={locked}
        index={index}
        count={deck.length}
        headingRef={headingRef}
        onNext={() => {
          setLocked(null);
          setValue(50);
          setTouched(false);
          setIndex(index + 1);
        }}
      />
    );
  }

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">
          Clue {index + 1} of {deck.length}
        </span>
        <span className="eyebrow hot">{total} pts</span>
      </div>
      <div className="body-area">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Clue {index + 1}
        </h1>
        <div className="cluecard">
          <span className="eyebrow">Someone else’s clue</span>
          <p>{card[2]}</p>
        </div>
        <p className="readout">
          <small>Your guess</small>
          {dial10(value)}
        </p>
        <DialFace
          value={value}
          poles={[card[0], card[1]]}
          onChange={(v) => {
            setValue(v);
            setTouched(true);
          }}
        />
        <p className="hint">Where did they mean?</p>
      </div>
      <div className="actions">
        <button
          type="button"
          className="btn"
          disabled={!touched}
          onClick={() => {
            const guess = quantise(value);
            const points = pointsFor(guess, card[3]);
            setLog([...log, { clue: card[2], guess, target: card[3], points }]);
            setLocked(guess);
            announce(`Locked in ${dial10(guess)}. You scored ${points}.`);
          }}
        >
          {touched ? 'Lock in guess' : 'Move the needle first'}
        </button>
      </div>
    </section>
  );
}

function SoloReveal({
  card,
  guess,
  index,
  count,
  headingRef,
  onNext,
}: {
  card: SoloCard;
  guess: number;
  index: number;
  count: number;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  onNext: () => void;
}) {
  const [landed, setLanded] = useState(false);
  const swept = useSweep(card[3], guess, () => setLanded(true));
  const points = useMemo(() => pointsFor(guess, card[3]), [guess, card]);

  useEffect(() => setLanded(false), [card]);

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">
          Clue {index + 1} of {count}
        </span>
        <span className="eyebrow hot">
          {points > 0 ? '+' : ''}
          {points}
        </span>
      </div>
      <div className="body-area top">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Reveal
        </h1>
        <p className="hint">{landed ? `They meant ${dial10(card[3])} · you said ${dial10(guess)}` : 'Finding the target…'}</p>
        <DialFace
          value={swept}
          poles={[card[0], card[1]]}
          target={landed ? card[3] : null}
          markers={landed ? [{ value: guess, label: 'YOU' }] : []}
          label="The target"
        />
        {/* The clue again, because the player is being argued with about
            something that is no longer on screen — and the author's reasoning
            only after the needle lands, or it steps on the one orchestrated
            moment in the app. */}
        <div className="cluecard">
          <span className="eyebrow">The clue</span>
          <p>{card[2]}</p>
        </div>
        {landed && (
          <div className="cluecard">
            <span className="eyebrow">Why</span>
            <p>{card[4]}</p>
          </div>
        )}
      </div>
      <div className="actions">
        <button type="button" className="btn" onClick={onNext}>
          {index + 1 >= count ? 'See your score' : 'Next clue'}
        </button>
      </div>
    </section>
  );
}

/**
 * Solo's first player is alone; this is how they invite the others. Uses the
 * Web Share sheet where it exists, the clipboard where it doesn't.
 */
function ShareScore({ total }: { total: number }) {
  const [said, setSaid] = useState<string | null>(null);
  const text = `I scored ${total}/40 on Needle solo.\nSee if you can read a stranger's mind better than I can.\n${location.origin}`;

  return (
    <button
      type="button"
      className="btn"
      onClick={async () => {
        try {
          if (navigator.share) {
            await navigator.share({ text });
            return;
          }
          await navigator.clipboard.writeText(text);
          setSaid('Copied — paste it to the group thread.');
        } catch {
          setSaid('Copy failed. Long-press to copy the link instead.');
        }
      }}
    >
      {said ?? 'Share your score'}
    </button>
  );
}
